/**
 * API client for dance transcription backend
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export type DanceSource = 'AI' | 'MANUAL';
export type DanceDifficulty = 'beginner' | 'intermediate' | 'advanced';
export type DanceStyle = 'contra' | 'square' | 'english-country' | 'other';
export type EnergyLevel = 'low' | 'medium' | 'high';
export type ProgramRole = 'opener' | 'filler' | 'closer';

export interface Dance {
  danceId: string;
  title: string;
  choreographer?: string;
  source: DanceSource;
  formation: string;
  a1: string;
  a2: string;
  b1: string;
  b2: string;
  difficulty?: DanceDifficulty;
  style?: DanceStyle;
  notes?: string;
  tags?: string[];
  moves?: string[];
  music?: string;
  musicNotes?: string;
  musicSource?: string;
  callerNotes?: string;
  teachingFigures?: string[];
  energyLevel?: EnergyLevel;
  programRole?: ProgramRole;
  userId?: string;
  username?: string;
  aiModel?: string;
  aiPrompt?: string;
  generationCost?: number;
  seedDanceIds?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateDanceRequest {
  title: string;
  choreographer?: string;
  formation: string;
  a1: string;
  a2: string;
  b1: string;
  b2: string;
  difficulty?: DanceDifficulty;
  style?: DanceStyle;
  notes?: string;
  tags?: string[];
  music?: string;
  musicNotes?: string;
  musicSource?: string;
  callerNotes?: string;
  teachingFigures?: string[];
  energyLevel?: EnergyLevel;
  programRole?: ProgramRole;
}

export interface GenerateDanceRequest {
  prompt: string;
  difficulty?: DanceDifficulty;
  style?: DanceStyle;
  seedDanceIds?: string[];
  energyLevel?: EnergyLevel;
  programRole?: ProgramRole;
}

export interface UpdateDanceRequest {
  title?: string;
  choreographer?: string;
  formation?: string;
  a1?: string;
  a2?: string;
  b1?: string;
  b2?: string;
  difficulty?: DanceDifficulty;
  style?: DanceStyle;
  notes?: string;
  tags?: string[];
  music?: string;
  musicNotes?: string;
  musicSource?: string;
  callerNotes?: string;
  teachingFigures?: string[];
  energyLevel?: EnergyLevel;
  programRole?: ProgramRole;
}

export interface GenerateDanceResponse {
  dance: Dance;
  tokensUsed: number;
  generationTimeMs: number;
  validationScore?: number;
  attempt?: number;
}

export interface ListDancesResponse {
  dances: Dance[];
}

export interface User {
  userId: string;
  username: string;
  displayName: string;
  createdAt: string;
  updatedAt: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  password: string;
  displayName: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface RegisterResponse {
  token: string;
  user: User;
}

export interface GoogleAuthRequest {
  credential: string;
}

class ApiError extends Error {
  constructor(public statusCode: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_URL}${endpoint}`;

  // Get token from localStorage
  const token = localStorage.getItem('token');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string> || {})
  };

  // Add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new ApiError(response.status, error.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export const danceApi = {
  /**
   * Create a manual dance
   */
  async createDance(data: CreateDanceRequest): Promise<Dance> {
    const response = await request<{ dance: Dance }>('/dances', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response.dance;
  },

  /**
   * Generate a dance using AI
   */
  async generateDance(data: GenerateDanceRequest): Promise<GenerateDanceResponse> {
    return await request<GenerateDanceResponse>('/dances/generate', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  /**
   * Get a single dance by ID
   */
  async getDance(danceId: string): Promise<Dance> {
    const response = await request<{ dance: Dance }>(`/dances/${danceId}`);
    return response.dance;
  },

  /**
   * List dances with optional filtering
   */
  async listDances(options?: { source?: DanceSource | 'ALL'; limit?: number }): Promise<Dance[]> {
    const params = new URLSearchParams();
    if (options?.source) {
      params.append('source', options.source);
    }
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await request<ListDancesResponse>(`/dances${query}`);
    return response.dances;
  },

  /**
   * Update an existing dance
   */
  async updateDance(danceId: string, data: UpdateDanceRequest): Promise<Dance> {
    return await request<Dance>(`/dances/${danceId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  /**
   * Delete a dance
   */
  async deleteDance(danceId: string): Promise<void> {
    await request<{ message: string }>(`/dances/${danceId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Register a new user
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    return await request<RegisterResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  /**
   * Login user
   */
  async login(data: LoginRequest): Promise<LoginResponse> {
    return await request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async googleAuth(data: GoogleAuthRequest): Promise<LoginResponse> {
    return await request<LoginResponse>('/auth/google', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }
};

export interface ProgramDanceSlot {
  danceId: string;
  position: number;
  callerNotes?: string;
}

export interface Program {
  programId: string;
  title: string;
  description?: string;
  userId: string;
  username: string;
  isPublic: boolean;
  publicId: string;
  dances: ProgramDanceSlot[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateProgramRequest {
  title: string;
  description?: string;
  dances?: ProgramDanceSlot[];
}

export interface UpdateProgramRequest {
  title?: string;
  description?: string;
  isPublic?: boolean;
  dances?: ProgramDanceSlot[];
}

export interface GenerateProgramRequest {
  prompt: string;
  experienceLevel?: 'beginner' | 'mixed' | 'experienced';
  danceCount?: number;
  energyArc?: 'gentle' | 'building' | 'high-energy';
}

export const programApi = {
  async createProgram(data: CreateProgramRequest): Promise<Program> {
    const response = await request<{ program: Program }>('/programs', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response.program;
  },

  async listPrograms(): Promise<Program[]> {
    const response = await request<{ programs: Program[] }>('/programs');
    return response.programs;
  },

  async getProgram(programId: string): Promise<Program> {
    const response = await request<{ program: Program }>(`/programs/${programId}`);
    return response.program;
  },

  async updateProgram(programId: string, data: UpdateProgramRequest): Promise<Program> {
    const response = await request<{ program: Program }>(`/programs/${programId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return response.program;
  },

  async deleteProgram(programId: string): Promise<void> {
    await request<{ message: string }>(`/programs/${programId}`, { method: 'DELETE' });
  },

  async getPublicProgram(publicId: string): Promise<Program> {
    const response = await request<{ program: Program }>(`/programs/public/${publicId}`);
    return response.program;
  },

  async generateProgram(data: GenerateProgramRequest): Promise<Program> {
    const response = await request<{ program: Program }>('/programs/generate', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response.program;
  }
};
